<script setup lang="ts">
/**
 * 사업자정보(W-FO-15) — design-prototypes/legal-business.html 이관.
 * layout-doc(중앙 780px) + page-title(serif h1) + doc-meta + 본문.
 *
 * 본문은 통신판매업 의무 고지 항목 — brand.legal(확정 실데이터, SSoT)을 정의 테이블로 렌더한다.
 * 푸터 사업자 정보와 동일 출처를 공유하므로 값이 한 곳(brand.legal)에서만 관리된다.
 */
import { useHead } from '@unhead/vue';

import { PageShell, PageTitle } from '#/components/ui';
import { brand } from '#/config/brand';
import { BUSINESS_META } from '#/data/legal';

useHead({ title: `${BUSINESS_META.title} · ${brand.name}` });

interface BusinessRow {
  label: string;
  value: string;
  /** tel: 링크로 렌더 */
  tel?: boolean;
  /** mailto: 링크로 렌더 */
  mail?: boolean;
}

/** 통신판매업 의무 고지 항목(brand.legal 실데이터 매핑). */
const rows: BusinessRow[] = [
  { label: '상호명', value: brand.legal.company },
  { label: '대표자', value: brand.legal.ceo },
  { label: '사업자등록번호', value: brand.legal.bizNo },
  { label: '통신판매업신고번호', value: brand.legal.mailOrderNo },
  { label: '주소', value: brand.legal.address },
  // 대표전화는 미개통 시 값이 비므로 행 자체를 내린다(brand.legal.tel 주석 참조).
  ...(brand.legal.tel ? [{ label: '대표전화', value: brand.legal.tel, tel: true }] : []),
  { label: '이메일', value: brand.legal.email, mail: true },
];
</script>

<template>
  <PageShell variant="legal">
    <PageTitle :title="BUSINESS_META.title" />

    <p class="mb-6 text-[12.5px] text-muted">{{ BUSINESS_META.meta }}</p>

    <dl
      class="overflow-hidden rounded-card border border-line bg-white shadow-sm"
      aria-label="사업자 정보"
    >
      <div
        v-for="(row, i) in rows"
        :key="row.label"
        class="grid grid-cols-1 gap-1 px-5 py-4 md:grid-cols-[160px_1fr] md:gap-4 md:px-7"
        :class="i < rows.length - 1 ? 'border-b border-line' : ''"
      >
        <dt class="text-[13px] font-bold text-brand">{{ row.label }}</dt>
        <dd class="m-0 min-w-0 break-words text-[14px] leading-[1.6] text-body">
          <a
            v-if="row.tel"
            :href="`tel:${row.value.replace(/[^0-9+]/g, '')}`"
            class="text-body transition-colors hover:text-brand"
          >
            {{ row.value }}
          </a>
          <a
            v-else-if="row.mail"
            :href="`mailto:${row.value}`"
            class="break-all text-body transition-colors hover:text-brand"
          >
            {{ row.value }}
          </a>
          <template v-else>{{ row.value }}</template>
        </dd>
      </div>
    </dl>
  </PageShell>
</template>
